/*
 * @Descripttion:
 * @version:
 * @Author: 周志国
 * @Date: 2025-11-27 15:14:21
 * @LastEditors: 周志国
 * @LastEditTime: 2025-12-01 15:44:33
 * @changeLog:
 * @FilePath: /anov-3d-webgl/packages/anov-3d-ability/src/business/Poi/panel/index.ts
 */
import { CSS3DObject, MathUtils, type PerspectiveCamera, type SceneControl, Vector3 } from '@anov/3d-core'
import ObjectLayer from '../../Base/ObjectLayer'
import { arr2, arr3, arr4, clampAlign, color, num, parseStyle, parseWeight, rep, sanitizeUrl, setStyles } from './utils'
import type { InfoPanelConfig, InfoPanelUpdateConfig, NineSliceConfig } from './interfaces'

/**
 * 文件概览
 * InfoPanel 是一个基于 CSS3DObject 的信息面板组件，用于在三维场景中以“屏幕像素”为基础进行排版和渲染。
 * - 最小像素尺寸通过 `size` 指定（默认 320x180），容器会随内容自适应增大。
 * - `pixelLock` 为 true 时，面板会随相机距离自动缩放以保持屏幕像素大小恒定。
 * - `scale`/`userScale3` 提供统一或非均匀的用户缩放，参与最终缩放计算。
 * - `billboard` 支持朝向相机（`yaw` 或 `full`），用于看板效果。
 */

/**
 * 组件默认配置。
 * - text: 标题（主文本）相关样式与背景设置
 * - content: 副文本显示与样式
 * - size: 面板最小像素尺寸 `[width, height]`
 * - scale: 统一用户缩放系数（>0）
 * - userScale3: 非均匀用户缩放 `[sx, sy, sz]`，与 `scale` 相乘
 * - pixelLock: 是否锁定屏幕像素尺寸
 * - billboard: 看板模式（'none'|'yaw'|'full'）
 * - anchor: 面板锚点（'top'|'center'|'bottom'）
 */
const defaults: InfoPanelConfig = {
  text: {
    value: '',
    fontSize: 16,
    color: '#ffffff',
    fontWeight: 'normal',
    fontStyle: 'normal',
    offset: [0, 0] as [number, number],
    align: 'left',
    spacing: 0,
    bgUrl: '',
    hsl: [200, 120, 80],
    bgNineSlice: {
      enabled: true,
      slice: [65, 92, 27, 17] as [number, number, number, number],
      width: [65, 92, 27, 17] as [number, number, number, number],
      repeat: 'stretch',
      fill: true,
      radius: 0,
    },
    bgSlice: [65, 92, 27, 17] as [number, number, number, number],
  },
  content: {
    visible: false,
    value: '',
    fontSize: 16,
    color: '#ffffff',
    fontWeight: 'normal',
    fontStyle: 'normal',
    align: 'left',
    offset: [0, 0] as [number, number],
    spacing: 0,
  },
  // 默认面板像素尺寸（宽度，高度）
  size: [320, 180] as [number, number],
  scale: 1,
  // 非均匀用户缩放（来自外部 scale: [x,y,z]）
  userScale3: [1, 1, 1] as [number, number, number],
  // 屏幕像素锁定：保持屏幕像素尺寸恒定
  pixelLock: true,
  billboard: 'none',
  anchor: 'top',
}

/**
 * InfoPanel
 * 一个基于 CSS3DObject 的三维信息面板。面板的排版以像素为单位，
 * 并可根据相机参数在 3D 世界中动态映射为适当的世界尺寸。
 *
 * 关键特性：
 * - 像素锁定（`pixelLock`）：保证屏幕像素尺寸恒定的看板效果。
 * - 动态缩放（`updateDynamicScale`）：实时根据相机 FOV、距离、视口宽高计算世界尺度。
 * - 看板模式（`billboard`）：支持水平（yaw）和全方位（full）朝向相机。
 * - 自定义缩放（`scale` 与 `userScale3`）：统一与非均匀缩放相乘得到最终缩放。
 *
 * 用法概览：
 * const panel = new InfoPanel({
 *   text: { value: 'POI A', fontSize: 18 },
 *   size: [320, 180],
 *   scale: 1,
 *   pixelLock: true,
 *   billboard: 'yaw',
 *   anchor: 'top'
 * });
 * panel.updateDynamicScale(camera);
 */
export default class InfoPanel extends ObjectLayer {
  // 新增字段声明，修复类型报错
  /**
   * 内部状态与元素引用
   * - config: 当前配置（已合并 defaults 与外部传入）
   * - container: 面板根容器（用于 CSS3DObject 承载）
   * - titleEl: 标题元素
   * - contentEl: 内容元素（可选）
   * - innerWrap: 内部包裹，用于实现锚点与偏移
   * - _cssObject: three.js CSS3DObject（加入到 scene graph）
   */
  private config: InfoPanelConfig
  private container: HTMLDivElement
  private titleEl: HTMLDivElement
  private contentEl: HTMLDivElement
  private innerWrap: HTMLDivElement
  private _cssObject: CSS3DObject
  // 复用的临时向量，避免频繁创建对象
  private _tmpObjPos: Vector3 = new Vector3()
  private _tmpCamPos: Vector3 = new Vector3()
  private _tmpCamDir: Vector3 = new Vector3()

  /**
   * 构造函数
   * @param cfg 外部配置对象；仅识别本组件支持的字段，其他字段会忽略
   */
  constructor(cfg: InfoPanelUpdateConfig = {}, sceneControl: SceneControl) {
    super(sceneControl)
    this.config = JSON.parse(JSON.stringify(defaults))
    this.updateConfig(cfg)
    this.container = document.createElement('div')
    this.titleEl = document.createElement('div')
    this.contentEl = document.createElement('div')
    this.innerWrap = document.createElement('div')
    // 内层包裹用于布局，默认采用容器左上角为参考点
    setStyles(this.innerWrap, {
      position: 'relative',
      left: '0',
      top: '0',
      transform: 'none',
      pointerEvents: 'auto',
    })
    this.innerWrap.appendChild(this.titleEl)
    this.innerWrap.appendChild(this.contentEl)
    this.container.appendChild(this.innerWrap)
    // 将 CSS3DObject 作为子节点添加，而非赋值到 this.object3D
    this._cssObject = new CSS3DObject(this.container)
    this.add(this._cssObject)
    this.applyStyle()
  }

  /**
   * 将传入的配置与默认值合并并规范化。
   * - 自动处理文本样式、九宫格背景、可见性、对齐与偏移。
   * - `scale`: 支持数值（统一缩放）或长度为 3 的数组（非均匀缩放）。
   * - `pixelLock`: 是否锁定屏幕像素尺寸。
   * - `anchor`: 面板锚点。
   * @param cfg 配置对象
   */
  updateConfig(cfg: InfoPanelUpdateConfig = {}) {
    const t = cfg.text || {}
    const c = cfg.content || {}
    this.config.text.value = typeof t.value === 'string' ? t.value : this.config.text.value
    this.config.text.fontSize = num(t.fontSize, this.config.text.fontSize)
    this.config.text.color = color(t.color, this.config.text.color)
    this.config.text.fontWeight = parseWeight(t.fontWeight || this.config.text.fontWeight)
    this.config.text.fontStyle = parseStyle(t.fontStyle || this.config.text.fontStyle)
    this.config.text.offset = arr2(t.offset, this.config.text.offset) as [number, number]
    this.config.text.align = clampAlign(t.align || this.config.text.align)
    this.config.text.spacing = num(t.spacing, this.config.text.spacing)
    this.config.text.bgUrl = typeof t.bgUrl === 'string' ? sanitizeUrl(t.bgUrl) : this.config.text.bgUrl
    this.config.text.hsl = arr3(t.hsl, this.config.text.hsl).slice(0, 3) as [number, number, number]
    const ns: Partial<NineSliceConfig> = t.bgNineSlice || {}
    this.config.text.bgNineSlice.enabled = typeof ns.enabled === 'boolean' ? ns.enabled : this.config.text.bgNineSlice.enabled
    this.config.text.bgNineSlice.slice = arr4(ns.slice || t.bgSlice, this.config.text.bgNineSlice.slice) as [number, number, number, number]
    this.config.text.bgNineSlice.width = arr4(ns.width || t.bgSlice, this.config.text.bgNineSlice.width) as [number, number, number, number]
    this.config.text.bgNineSlice.repeat = rep(ns.repeat || this.config.text.bgNineSlice.repeat) as NineSliceConfig['repeat']
    this.config.text.bgNineSlice.fill = typeof ns.fill === 'boolean' ? ns.fill : this.config.text.bgNineSlice.fill
    this.config.text.bgNineSlice.radius = num(ns.radius, this.config.text.bgNineSlice.radius)
    this.config.content.visible = typeof c.visible === 'boolean' ? c.visible : this.config.content.visible
    this.config.content.value = typeof c.value === 'string' ? c.value : this.config.content.value
    this.config.content.fontSize = num(c.fontSize, this.config.content.fontSize)
    this.config.content.color = color(c.color, this.config.content.color)
    this.config.content.fontWeight = parseWeight(c.fontWeight || this.config.content.fontWeight)
    this.config.content.fontStyle = parseStyle(c.fontStyle || this.config.content.fontStyle)
    this.config.content.align = clampAlign(c.align || this.config.content.align)
    this.config.content.offset = arr2(c.offset, this.config.content.offset) as [number, number]
    this.config.content.spacing = num(c.spacing, this.config.content.spacing)
    // 支持数值或数组。数值作为统一倍数，数组作为非均匀倍数
    if (Array.isArray(cfg.scale) && cfg.scale.length === 3) {
      const sx = num(cfg.scale[0], this.config.userScale3[0])
      const sy = num(cfg.scale[1], this.config.userScale3[1])
      const sz = num(cfg.scale[2], this.config.userScale3[2])
      this.config.userScale3 = [sx, sy, sz]
    }
    else {
      const s = num(cfg.scale, this.config.scale)
      // 用户缩放因子：仅作为乘数，不参与单位换算
      this.config.scale = s > 0 ? s : this.config.scale
    }
    const bb = (cfg.billboard || this.config.billboard)
    this.config.billboard = bb === 'yaw' || bb === 'full' ? bb : 'none'
    // 面板尺寸可通过 cfg.size 指定，否则使用默认尺寸
    this.config.size = arr2(cfg.size, this.config.size) as [number, number]
    // 屏幕像素锁定
    this.config.pixelLock = !!(typeof cfg.pixelLock === 'boolean' ? cfg.pixelLock : this.config.pixelLock)
    // 面板锚点
    const ac = cfg.anchor || this.config.anchor
    this.config.anchor = ac === 'top' || ac === 'center' || ac === 'bottom' ? ac : 'bottom'
  }

  /**
   * 设置面板世界坐标（应用到 CSS3DObject）。
   */
  setPosition(x: number, y: number, z: number): void {
    this._cssObject.position.set(x, y, z)
  }

  /**
   * 设置面板世界旋转（欧拉角，单位：弧度）。
   */
  setRotation(x: number, y: number, z: number): void {
    this._cssObject.rotation.set(x, y, z)
  }

  /**
   * 设置统一缩放因子（立即应用到 CSS3DObject）。
   * 注意：若使用 `updateDynamicScale`，每帧会覆盖此值。
   */
  setScale(s: number): void {
    this._cssObject.scale.setScalar(s)
  }

  /**
   * 设置看板模式。
   * @param mode 'none' | 'yaw' | 'full'
   */
  setBillboard(mode: 'none' | 'yaw' | 'full'): void {
    const m = mode === 'yaw' || mode === 'full' ? mode : 'none'
    this.config.billboard = m
  }

  /**
   * 设置可见性。
   * @param v 是否可见
   */
  setVisible(v: boolean): void {
    this._cssObject.visible = !!v
  }

  /**
   * 应用所有样式与文本到 DOM。
   * 仅影响面板的排版与外观，不涉及 3D 变换。
   */
  applyStyle(): void {
    const t = this.config.text
    const c = this.config.content
    // 基础容器样式，确保可见尺寸与居中锚点
    setStyles(this.container, {
      display: 'inline-block',
      boxSizing: 'border-box',
      transformOrigin: 'center center',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backfaceVisibility: 'hidden',
      padding: '8px',
    })
    // 最小像素尺寸（默认 320x180，可通过 cfg.size 覆盖），容器尺寸随内容自适应
    const [pxW, pxH] = this.config.size
    setStyles(this.container, {
      width: '',
      height: '',
      minWidth: `${pxW}px`,
      minHeight: `${pxH}px`,
    })
    // 根据锚点设置内部包裹的定位与偏移（默认 top：以左上角为参考）
    if (this.config.anchor === 'top') {
      this.innerWrap.style.left = '0'
      this.innerWrap.style.top = '0'
      this.innerWrap.style.bottom = 'auto'
      this.innerWrap.style.transform = 'none'
    }
    else if (this.config.anchor === 'center') {
      this.innerWrap.style.left = '50%'
      this.innerWrap.style.top = '50%'
      this.innerWrap.style.bottom = 'auto'
      this.innerWrap.style.transform = 'translate(-50%, -50%)'
    }
    else { // bottom
      this.innerWrap.style.left = '0'
      this.innerWrap.style.top = 'auto'
      this.innerWrap.style.bottom = '0'
      this.innerWrap.style.transform = 'none'
    }
    this.titleEl.textContent = t.value || ''
    setStyles(this.titleEl, {
      fontSize: `${t.fontSize}px`,
      color: t.color,
      fontWeight: t.fontWeight,
      fontStyle: t.fontStyle,
      textAlign: t.align,
      letterSpacing: `${num(t.spacing)}px`,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      marginLeft: `${num(t.offset[0])}px`,
      marginTop: `${num(t.offset[1])}px`,
    })
    this.contentEl.style.display = c.visible ? 'block' : 'none'
    this.contentEl.textContent = c.value || ''
    setStyles(this.contentEl, {
      fontSize: `${c.fontSize}px`,
      color: c.color,
      fontWeight: c.fontWeight,
      fontStyle: c.fontStyle,
      textAlign: c.align,
      letterSpacing: `${num(c.spacing)}px`,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      overflow: 'visible',
      textOverflow: 'clip',
      marginLeft: `${num(c.offset[0])}px`,
      marginTop: `${num(c.offset[1])}px`,
    })
    const hasBgUrl = typeof t.bgUrl === 'string' && t.bgUrl.length > 0
    if (hasBgUrl) {
      if (t.bgNineSlice && t.bgNineSlice.enabled) {
        const s = t.bgNineSlice.slice
        const w = t.bgNineSlice.width
        // 厚度限制，避免边框厚度超过容器尺寸导致异常视觉放大
        const wClamped = [
          Math.min(w[0], pxH * 0.5),
          Math.min(w[1], pxW * 0.5),
          Math.min(w[2], pxH * 0.5),
          Math.min(w[3], pxW * 0.5),
        ] as [number, number, number, number]
        setStyles(this.container, {
          borderStyle: 'solid',
          borderWidth: `${wClamped[0]}px ${wClamped[1]}px ${wClamped[2]}px ${wClamped[3]}px`,
          borderImageSource: `url(${t.bgUrl})`,
          borderImageSlice: `${s[0]} ${s[1]} ${s[2]} ${s[3]}${t.bgNineSlice.fill ? ' fill' : ''}`,
          borderImageRepeat: t.bgNineSlice.repeat,
          backgroundImage: 'none',
          borderRadius: `${num(t.bgNineSlice.radius, 0)}px`,
        })
      }
      else {
        setStyles(this.container, {
          backgroundImage: `url(${t.bgUrl})`,
          borderImageSource: 'none',
          borderImageSlice: '',
          borderImageRepeat: '',
          borderStyle: 'none',
          borderWidth: '0px',
        })
      }
    }
    else {
      setStyles(this.container, {
        backgroundImage: 'none',
        borderImageSource: 'none',
        borderImageSlice: '',
        borderImageRepeat: '',
        borderStyle: 'none',
        borderWidth: '0px',
      })
    }
    // 背景填充整个容器尺寸
    setStyles(this.container, {
      backgroundSize: '100% 100%',
      textAlign: t.align,
      pointerEvents: 'auto',
      filter: `hue-rotate(${num(t.hsl[0], 0)}deg) saturate(${num(t.hsl[1], 0)}%) brightness(${num(t.hsl[2], 0)}%)`,
    })
  }

  /**
   * 更新配置并立即重新应用到 DOM。
   * @param cfg 新配置
   */
  update(cfg: InfoPanelUpdateConfig = {}): void {
    this.updateConfig(cfg)
    this.applyStyle()
  }

  /**
   * 根据看板模式使面板朝向相机。
   * - 'full'：使用 `lookAt` 全方位朝向相机。
   * - 'yaw'：仅绕 Y 轴旋转，保持水平面朝向相机。
   * - 'none'：不处理朝向。
   * @param camera three.js 相机
   */
  updateFacing(camera: PerspectiveCamera): void {
    const mode = this.config.billboard
    if (mode === 'none')
      return
    const objPos = this._tmpObjPos
    const camPos = this._tmpCamPos
    this._cssObject.getWorldPosition(objPos)
    camera.getWorldPosition(camPos)
    if (mode === 'full') {
      this._cssObject.lookAt(camPos)
    }
    else if (mode === 'yaw') {
      const dx = camPos.x - objPos.x
      const dz = camPos.z - objPos.z
      const yaw = Math.atan2(dx, dz)
      this._cssObject.rotation.set(0, yaw, 0)
    }
  }

  /**
   * 动态缩放：根据相机 FOV、沿相机前向的距离 d、视口尺寸，
   * 计算“每像素对应的世界单位”，并结合用户缩放得到最终缩放。
   * - pixelLock: s = worldPerPixelX * userScale
   * - 非锁定: s = userScale
   * - 最终各轴缩放: [sx, sy, sz] = s * userScale3
   */
  updateDynamicScale(camera: PerspectiveCamera, viewportWidth = window.innerWidth, viewportHeight = window.innerHeight): void {
    if (!camera)
      return
    const objPos = this._tmpObjPos
    const camPos = this._tmpCamPos
    const camDir = this._tmpCamDir
    this._cssObject.getWorldPosition(objPos)
    camera.getWorldPosition(camPos)
    camera.getWorldDirection(camDir)
    const d = objPos.clone().sub(camPos).dot(camDir)
    const fovRad = MathUtils.degToRad(camera.fov)
    const worldPerPixelY = (2 * d * Math.tan(fovRad / 2)) / viewportHeight
    const worldPerPixelX = worldPerPixelY * camera.aspect
    const userScale = this.config.scale
    const userScale3 = this.config.userScale3 || [1, 1, 1]
    // pixel-lock：保持屏幕像素尺寸恒定；非锁定：使用统一用户系数
    let s = this.config.pixelLock
      ? worldPerPixelX * (userScale > 0 ? userScale : 1)
      : (userScale > 0 ? userScale : 1)
    if (!Number.isFinite(s) || s <= 0)
      return
    // 简单限幅，避免极端相机参数造成缩放异常
    const MAX_S = 1e3
    const MIN_S = 1e-6
    s = Math.max(Math.min(s, MAX_S), MIN_S)
    const sx = s * (userScale3[0] || 1)
    const sy = s * (userScale3[1] || 1)
    const sz = s * (userScale3[2] || 1)
    this._cssObject.scale.set(sx, sy, sz)
  }

  // 移除父级缩放抵消，面板可响应外部实体缩放（含非均匀）

  /**
   * 释放面板：从场景移除 CSS3DObject，并清理 DOM。
   */
  dispose(): void {
    if (this._cssObject.parent)
      this._cssObject.parent.remove(this._cssObject)
    this.container.remove()
  }

  /**
   * 获取当前面板在世界坐标中的宽高（单位：世界单位）。
   * 注意：结果受相机参数、面板到相机的距离以及当前 `_cssObject.scale` 影响。
   * @param camera three.js 相机
   * @param viewportWidth 视口宽度（像素）
   * @param viewportHeight 视口高度（像素）
   * @returns `{ width, height }` 世界单位
   */
  getWorldSize(camera: PerspectiveCamera, viewportWidth = window.innerWidth, viewportHeight = window.innerHeight): { width: number; height: number } {
    const objPos = this._tmpObjPos
    const camPos = this._tmpCamPos
    const camDir = this._tmpCamDir
    this._cssObject.getWorldPosition(objPos)
    camera.getWorldPosition(camPos)
    camera.getWorldDirection(camDir)
    const d = objPos.clone().sub(camPos).dot(camDir)
    const fovRad = MathUtils.degToRad(camera.fov)
    const worldPerPixelY = (2 * d * Math.tan(fovRad / 2)) / viewportHeight
    const worldPerPixelX = worldPerPixelY * camera.aspect
    const wpx = this.container.offsetWidth || 0
    const hpx = this.container.offsetHeight || 0
    const s = this._cssObject.scale.x
    return { width: wpx * worldPerPixelX * s, height: hpx * worldPerPixelY * s }
  }
}
